import { useMemo } from 'react';
import { DataTable, DataTableBody } from '@optiaxiom/react';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';

import { Tool } from '@frontend/types';

import createColumns from './columns';
import { ToolsTableRow } from './types';

type ToolsTableProps = {
  tools: Tool[];
  isUpdating: boolean;
  updatingToolId: string | null | undefined;
  isUpdatingEnabledInPrimaryAgent: boolean;
  updatingToolIdEnabledInPrimaryAgent: string | null | undefined;
  onToggleActive: (toolId: string, isDisabled: boolean) => void;
  onToggleEnabledInPrimaryAgent: (toolName: string, isEnabledInPrimaryAgent: boolean) => void;
  onBulkToggle: (serviceId: string, functionIds: string[], action: 'enable' | 'disable') => void;
  isBulkToggling: boolean;
  onBulkToggleChatEnabled: (toolNames: string[], action: 'enable' | 'disable') => void;
  isBulkTogglingChat: boolean;
};

export function ToolsTable({
  tools,
  isUpdating,
  updatingToolId,
  isUpdatingEnabledInPrimaryAgent,
  updatingToolIdEnabledInPrimaryAgent,
  onToggleActive,
  onToggleEnabledInPrimaryAgent,
  onBulkToggle,
  isBulkToggling,
  onBulkToggleChatEnabled,
  isBulkTogglingChat,
}: ToolsTableProps) {
  const enabledToolCount = useMemo(
    () => tools.filter(tool => tool.is_enabled_in_primary_agent).length,
    [tools]
  );

  const columns = useMemo(
    () =>
      createColumns({
        enabledToolCount,
        isUpdating,
        updatingToolId,
        isUpdatingEnabledInPrimaryAgent,
        updatingToolIdEnabledInPrimaryAgent,
        onToggleActive,
        onToggleEnabledInPrimaryAgent,
        onBulkToggle,
        isBulkToggling,
        onBulkToggleChatEnabled,
        isBulkTogglingChat,
      }),
    [
      enabledToolCount,
      isUpdating,
      updatingToolId,
      isUpdatingEnabledInPrimaryAgent,
      updatingToolIdEnabledInPrimaryAgent,
      onToggleActive,
      onToggleEnabledInPrimaryAgent,
      onBulkToggle,
      isBulkToggling,
      onBulkToggleChatEnabled,
      isBulkTogglingChat,
    ]
  );

  const data = useMemo(() => {
    const registryMap = new Map<string, ToolsTableRow>();
    tools.forEach(tool => {
      const registryId = tool.service.id;
      if (!registryMap.has(registryId)) {
        registryMap.set(registryId, {
          id: tool.service.id,
          name: tool.service.name,
          isRegistry: true,
          registry: tool.service,
          items: [
            {
              id: tool.function_id,
              name: tool.name,
              isRegistry: false,
              auth_status: tool.auth_status,
              tool: tool,
              items: [],
            },
          ],
        });
        return;
      }
      registryMap.get(registryId)?.items.push({
        id: tool.function_id,
        name: tool.name,
        isRegistry: false,
        auth_status: tool.auth_status,
        tool: tool,
        items: [],
      });
    });

    return Array.from(registryMap.values());
  }, [tools]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => row.items,
  });

  return (
    <DataTable table={table}>
      <DataTableBody />
    </DataTable>
  );
}
